import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft, 
  ArrowRight, 
  User, 
  Mail, 
  Phone, 
  CreditCard,
  Camera,
  Video,
  CheckCircle,
  PartyPopper
} from 'lucide-react';

const STEPS = [
  { id: 'form', title: 'Datos Personales', icon: User },
  { id: 'verify', title: 'Verificación SMS', icon: Phone },
  { id: 'document', title: 'Foto Documento', icon: Camera },
  { id: 'video', title: 'Video Confirmación', icon: Video },
  { id: 'complete', title: 'Solicitud Enviada', icon: CheckCircle },
];

export default function ClaimPrizePage() {
  const { raffleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [code, setCode] = useState('');
  
  const [formData, setFormData] = useState({
    raffle_id: raffleId,
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    document_number: user?.document_number || '',
    phone: user?.phone || '',
    email: user?.email || '',
  });

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmitForm = async () => {
    setLoading(true);
    try {
      const result = await api.submitClaim(formData);
      setClaimId(result.id);
      setCurrentStep(1);
      toast.success('Datos enviados. Revisa tu SMS');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al enviar datos';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      toast.error('Ingresa el código completo');
      return;
    }
    
    setLoading(true);
    try {
      await api.verifyClaimCode(claimId, code);
      setCurrentStep(2);
      toast.success('Código verificado');
    } catch (error) {
      const message = error.response?.data?.detail || 'Código incorrecto';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    setLoading(true);
    try {
      await api.uploadClaimDocument(claimId);
      setCurrentStep(3);
      toast.success('Documento recibido');
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async () => {
    setLoading(true);
    try {
      await api.uploadClaimVideo(claimId);
      setCurrentStep(4);
      toast.success('Video recibido');
    } catch (error) {
      toast.error('Error al subir video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/')}
        className="mb-2"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Volver
      </Button>

      {/* Progress Steps */}
      <div className="flex items-center justify-between px-4">
        {STEPS.map((step, index) => (
          <div 
            key={step.id}
            className={`flex flex-col items-center ${index <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
              index < currentStep ? 'bg-primary text-white' :
              index === currentStep ? 'bg-primary/20 text-primary' :
              'bg-slate-100'
            }`}>
              {index < currentStep ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <step.icon className="w-4 h-4" />
              )}
            </div>
            <span className="text-xs hidden sm:block">{step.title}</span>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="card-base" data-testid={`claim-step-${currentStep}`}>
        {/* Step 0: Form */}
        {currentStep === 0 && (
          <>
            <CardHeader>
              <CardTitle className="heading-4">Confirma tus datos</CardTitle>
              <CardDescription>
                Estos datos serán usados para transferirte el premio
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre</Label>
                  <Input
                    value={formData.first_name}
                    onChange={handleChange('first_name')}
                    className="input-base"
                    data-testid="claim-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Apellido</Label>
                  <Input
                    value={formData.last_name}
                    onChange={handleChange('last_name')}
                    className="input-base"
                    data-testid="claim-last-name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Documento</Label>
                <Input
                  value={formData.document_number}
                  onChange={handleChange('document_number')}
                  className="input-base"
                  data-testid="claim-document"
                />
              </div>

              <div className="space-y-2">
                <Label>Teléfono (para pago)</Label>
                <Input
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  className="input-base"
                  data-testid="claim-phone"
                />
              </div>

              <div className="space-y-2">
                <Label>Correo</Label>
                <Input
                  value={formData.email}
                  onChange={handleChange('email')}
                  className="input-base"
                  data-testid="claim-email"
                />
              </div>

              <Button
                onClick={handleSubmitForm}
                disabled={loading}
                className="w-full btn-primary h-12"
                data-testid="claim-submit-form"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Continuar <ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 1: SMS Verification */}
        {currentStep === 1 && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="heading-4">Verifica tu teléfono</CardTitle>
              <CardDescription>
                Ingresa el código de 6 dígitos enviado a {formData.phone}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <Button
                onClick={handleVerifyCode}
                disabled={loading || code.length !== 6}
                className="w-full btn-primary h-12"
                data-testid="claim-verify-code"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar'}
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 2: Document Upload */}
        {currentStep === 2 && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="heading-4">Foto de tu documento</CardTitle>
              <CardDescription>
                Toma una foto clara de tu documento de identidad
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  [MVP] Subida de foto simulada
                </p>
                <Button variant="outline" className="rounded-full">
                  <Camera className="w-4 h-4 mr-2" />
                  Tomar Foto
                </Button>
              </div>
              
              <Button
                onClick={handleUploadDocument}
                disabled={loading}
                className="w-full btn-primary h-12"
                data-testid="claim-upload-doc"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Continuar <ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 3: Video Upload */}
        {currentStep === 3 && (
          <>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="heading-4">Video de confirmación</CardTitle>
              <CardDescription>
                Graba un video corto (máx. 30 seg) diciendo:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-4 text-sm italic">
                "Mi nombre es <strong>{formData.first_name} {formData.last_name}</strong> y soy el ganador del sorteo de TuPrima. 
                Gané $1.000.000 y ya los tengo en mi cuenta. ¡Aquí está el comprobante!"
              </div>
              
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  [MVP] Subida de video simulada
                </p>
                <Button variant="outline" className="rounded-full">
                  <Video className="w-4 h-4 mr-2" />
                  Grabar Video
                </Button>
              </div>
              
              <Button
                onClick={handleUploadVideo}
                disabled={loading}
                className="w-full btn-primary h-12"
                data-testid="claim-upload-video"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar Solicitud'}
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <>
            <CardContent className="py-12 text-center">
              <PartyPopper className="w-20 h-20 mx-auto mb-6 text-primary" />
              <h2 className="heading-2 mb-4">¡Solicitud Enviada!</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Tu solicitud fue recibida. En un plazo máximo de <strong>30 minutos</strong> recibirás tu premio.
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Te notificaremos por SMS y correo cuando el pago sea procesado.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="btn-primary"
                data-testid="claim-done"
              >
                Volver al Inicio
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
